// Modelo puro da paginação compartilhada.
// Concentra a validação das props, a geração das páginas visíveis e a decisão
// sobre quais destinos podem ser emitidos. Os componentes apenas desenham o
// resultado. A base não guarda página nem consulta dados: o consumidor continua
// responsável pelo estado (contrato CT01).

/** Quantidade de páginas vizinhas exibidas de cada lado quando a prop é omitida ou inválida. */
export const DEFAULT_SIBLING_COUNT = 1;

/**
 * Maior `siblingCount` aceito. Limita a composição a 2 * 3 + 5 = 11 posições,
 * que ainda cabem no rodapé em 768 px sem rolagem horizontal.
 */
export const MAX_SIBLING_COUNT = 3;

export type PaginationPageItem = {
	type: "page";
	value: number;
	isCurrent: boolean;
};

export type PaginationEllipsisItem = {
	type: "ellipsis";
	key: number;
};

export type PaginationListItem = PaginationPageItem | PaginationEllipsisItem;

/**
 * - `known`: total de páginas inteiro, seguro e não negativo (zero é válido).
 * - `unknown`: total ausente (`null`), por exemplo antes da primeira resposta.
 * - `invalid`: valor recebido que não representa total de páginas.
 */
export type PaginationTotalStatus = "known" | "unknown" | "invalid";

export type PaginationModel = {
	/** Página atual válida dentro de `1..total`, ou `null` quando não há página confiável. */
	page: number | null;
	/** Total de páginas confirmado, ou `null` quando desconhecido ou inválido. */
	total: number | null;
	totalStatus: PaginationTotalStatus;
	/** `siblingCount` efetivamente aplicado. */
	siblingCount: number;
	/** Itens a desenhar: números e elipses, em ordem. */
	items: PaginationListItem[];
};

export type PaginationModelInput = {
	page: unknown;
	total: unknown;
	siblingCount?: unknown;
};

export const isPositiveSafeInteger = (value: unknown): value is number =>
	typeof value === "number" && Number.isSafeInteger(value) && value >= 1;

const isNonNegativeSafeInteger = (value: unknown): value is number =>
	typeof value === "number" && Number.isSafeInteger(value) && value >= 0;

export function normalizeSiblingCount(value: unknown): number {
	if (!isNonNegativeSafeInteger(value)) {
		return DEFAULT_SIBLING_COUNT;
	}
	return Math.min(value, MAX_SIBLING_COUNT);
}

const range = (start: number, end: number): number[] =>
	Array.from(
		{ length: Math.max(0, end - start + 1) },
		(_, index) => start + index
	);

/**
 * Gera os números visíveis para `total` páginas em torno de `anchor`.
 *
 * Com `total > 2 * siblingCount + 5`, a composição tem sempre 2 * siblingCount + 5
 * posições: primeira página, uma posição de borda, a janela de vizinhas, outra
 * posição de borda e a última página. A posição de borda vira elipse somente
 * quando esconde duas ou mais páginas; se esconderia uma só, mostra o número.
 */
export function createPaginationItems(
	total: number,
	anchor: number,
	currentPage: number | null,
	siblingCount: number
): PaginationListItem[] {
	if (total <= 0) {
		return [];
	}

	const toPage = (value: number): PaginationPageItem => ({
		type: "page",
		value,
		isCurrent: value === currentPage,
	});

	const maxPositions = siblingCount * 2 + 5;
	if (total <= maxPositions) {
		return range(1, total).map(toPage);
	}

	const windowStart = Math.max(
		Math.min(anchor - siblingCount, total - siblingCount * 2 - 2),
		3
	);
	const windowEnd = Math.min(
		Math.max(anchor + siblingCount, siblingCount * 2 + 3),
		total - 2
	);

	const items: PaginationListItem[] = [toPage(1)];
	items.push(
		windowStart > 3 ? { type: "ellipsis", key: windowStart - 1 } : toPage(2)
	);
	for (const value of range(windowStart, windowEnd)) {
		items.push(toPage(value));
	}
	items.push(
		windowEnd < total - 2
			? { type: "ellipsis", key: windowEnd + 1 }
			: toPage(total - 1)
	);
	items.push(toPage(total));
	return items;
}

/**
 * Normaliza as props recebidas em tempo de execução (contrato CT15).
 * TypeScript não impede `NaN`, frações, infinitos ou valores vindos de uma API.
 */
export function resolvePaginationModel({
	page,
	total,
	siblingCount,
}: PaginationModelInput): PaginationModel {
	const effectiveSiblingCount = normalizeSiblingCount(siblingCount);

	let totalStatus: PaginationTotalStatus;
	if (total === null || total === undefined) {
		totalStatus = "unknown";
	} else if (isNonNegativeSafeInteger(total)) {
		totalStatus = "known";
	} else {
		totalStatus = "invalid";
	}

	if (totalStatus !== "known") {
		return {
			page: null,
			total: null,
			totalStatus,
			siblingCount: effectiveSiblingCount,
			items: [],
		};
	}

	const knownTotal = total as number;
	const validPage =
		isPositiveSafeInteger(page) && page <= knownTotal ? page : null;
	// Sem página válida, a janela fica na posição mais próxima do valor recebido,
	// mas nenhum número é marcado como atual.
	let anchor = 1;
	if (validPage !== null) {
		anchor = validPage;
	} else if (isPositiveSafeInteger(page)) {
		anchor = Math.min(page, Math.max(knownTotal, 1));
	}

	return {
		page: validPage,
		total: knownTotal,
		totalStatus,
		siblingCount: effectiveSiblingCount,
		items: createPaginationItems(
			knownTotal,
			anchor,
			validPage,
			effectiveSiblingCount
		),
	};
}

/**
 * Um destino só pode ser emitido quando o total é conhecido, a página atual é
 * válida e o destino é outra página inteira dentro dos limites (contrato CT04).
 */
export function canNavigateTo(
	model: Pick<PaginationModel, "page" | "total">,
	target: unknown
): target is number {
	return (
		model.total !== null &&
		model.page !== null &&
		isPositiveSafeInteger(target) &&
		target <= model.total &&
		target !== model.page
	);
}

/** Destino de anterior ou próxima, ou `null` quando o passo não é permitido. */
export function getStepTarget(
	model: Pick<PaginationModel, "page" | "total">,
	direction: "prev" | "next"
): number | null {
	if (model.page === null) {
		return null;
	}
	const target = direction === "prev" ? model.page - 1 : model.page + 1;
	return canNavigateTo(model, target) ? target : null;
}

export function describePaginationStatus(
	model: Pick<PaginationModel, "page" | "total">
):
	| { kind: "page"; page: number; total: number }
	| { kind: "message"; text: string } {
	if (model.total === null) {
		return { kind: "message", text: "Total de páginas indisponível" };
	}
	if (model.total === 0) {
		return { kind: "message", text: "Nenhuma página de resultados" };
	}
	if (model.page === null) {
		return { kind: "message", text: "Página indisponível" };
	}
	return { kind: "page", page: model.page, total: model.total };
}
